/**
 * Billing — the shop's own account with SmartSpaza.
 *
 * Its own route rather than a Settings tab, though it was tempting to put it
 * there. Settings is where an owner configures the *shop*; this is a
 * relationship with a supplier, it has money and a due date in it, and burying
 * it four tabs deep is how an overdue invoice goes unseen until the account is
 * suspended.
 *
 * The whole route is behind `billing.view`, so a cashier does not see it in the
 * sidebar and cannot open it — see `NAV_ROUTES`. The tabs follow the Settings
 * pattern rather than inventing a second one.
 */

import { useState } from "react";

import { cn } from "@/lib/cn";

import { DailyPremium } from "./DailyPremium";
import { MyInvoices } from "./MyInvoices";
import { MySubscription } from "./MySubscription";
import { PremiumStatements } from "./PremiumStatements";

// The daily premium is first, and that ordering is the whole of this file's
// judgement. A package is something an owner thinks about twice a year; R30 a
// day is something they can be suspended over this week. The tab somebody lands
// on should be the one that can cost them their till.
const TABS = [
  { id: "premium", label: "Daily premium", render: () => <DailyPremium /> },
  {
    id: "statements",
    label: "Statements",
    render: () => <PremiumStatements />,
  },
  { id: "subscription", label: "My package", render: () => <MySubscription /> },
  { id: "invoices", label: "Invoices", render: () => <MyInvoices /> },
] as const;

export function BillingPage() {
  const [active, setActive] = useState<(typeof TABS)[number]["id"]>("premium");
  const current = TABS.find((tab) => tab.id === active) ?? TABS[0];

  return (
    <div className="space-y-5">
      <div
        role="tablist"
        aria-label="Billing sections"
        className="flex flex-wrap gap-1 border-b border-line"
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === current.id}
            onClick={() => setActive(tab.id)}
            className={cn(
              "-mb-px h-touch border-b-2 px-4 text-sm font-medium transition-colors",
              tab.id === current.id
                ? "border-brand-500 text-ink-900"
                : "border-transparent text-ink-500 hover:text-ink-900",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {current.render()}
    </div>
  );
}
